import { PageTitle } from "components/page-title";
import { type LucideIcon } from "lucide-react";

import { DynamicBreadcrumbsMenu } from "components/breadcrumbs/dynamic-breadcrumbs";
import { ThemeToggle } from "components/theme-toggle";
import { SidebarTrigger } from "@/components/ui/sidebar";

interface StandardPageLayoutProps {
  overlay?: boolean;
  pageTitle?: {
    icon: LucideIcon;
    title: string;
    description: string;
    actions?: React.ReactNode;
  };
  sidebar?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: "full" | "7xl" | "6xl";
  sidebarWidth?: "default" | "narrow";
}

export function StandardPageLayout({
  overlay,
  pageTitle,
  sidebar,
  children,
  maxWidth = "full",
  sidebarWidth = "default",
}: StandardPageLayoutProps) {
  const maxWidthClass = {
    full: "",
    "7xl": "max-w-7xl mx-auto",
    "6xl": "max-w-6xl mx-auto",
  }[maxWidth];

  return (
    <div className="flex flex-col flex-1 min-h-0" data-map-viewport={overlay ? "" : undefined}>
      <header
        data-map-overlay={overlay ? "top" : undefined}
        className={
          overlay
            ? "absolute top-0 left-0 right-0 z-10 flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60"
            : "flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12"
        }
      >
        <div className="flex items-center gap-2 w-full px-4">
          <SidebarTrigger className="-ml-1" />
          <DynamicBreadcrumbsMenu />
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>
      </header>
      {overlay ? (
        <main className="relative flex-1 min-h-0 overflow-hidden">
          {children}
        </main>
      ) : (
        <main className="flex-1 min-h-0 min-w-0 overflow-auto">
          <div className={`px-6 pb-6 ${maxWidthClass}`}>
            {sidebar ? (
              <div
                className={`grid gap-4 ${
                  sidebarWidth === "narrow"
                    ? "md:grid-cols-[160px_1fr]"
                    : "md:grid-cols-[220px_1fr]"
                }`}
              >
                <aside className="hidden md:block">
                  <div className="sticky top-0 space-y-6">{sidebar}</div>
                </aside>
                <div className="min-w-0 space-y-6">
                  {pageTitle && <PageTitle {...pageTitle} />}
                  {children}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {pageTitle && <PageTitle {...pageTitle} />}
                {children}
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
}
